import { useEffect, useRef, useState } from "react";
import { api, getApiError } from "../api";
import type { ImageReadbackComparison as Comparison, ProductReviewDetail } from "../types";

type Photo = {
  ready: boolean;
  failed: boolean;
  ref?: (node: HTMLImageElement | null) => void;
  onLoad?: (event: { currentTarget: HTMLImageElement }) => void;
  onError?: () => void;
};
type Inspection = {
  scope: string;
  active: boolean;
  checked: Set<number>;
  photos: Map<string, Photo>;
  generation: number;
  pending: boolean;
  refreshPending: boolean;
  saved: boolean;
  stale: boolean;
  error: string;
  refreshError: string;
};

function available(comparison: Comparison | null | undefined): comparison is Comparison {
  const present = (value: unknown) => typeof value === "string" && Boolean(value.trim());
  const imageUrl = (value: unknown) => typeof value === "string" && /^https?:\/\//i.test(value);
  return Boolean(comparison && present(comparison.jobId) && present(comparison.reviewToken)
    && Array.isArray(comparison.pairs) && comparison.pairs.length
    && comparison.pairs.every(pair => pair && present(pair.attribute) && present(pair.role)
      && imageUrl(pair.submittedUrl) && imageUrl(pair.amazonUrl)));
}

export function ImageReadbackComparison({ detail, onChanged, disabled = false }: { detail: ProductReviewDetail; onChanged: () => void | Promise<void>; disabled?: boolean }) {
  const comparison = available(detail.images.readbackComparison) ? detail.images.readbackComparison : null;
  const scope = JSON.stringify([detail.sellerSku, detail.marketplaceCode, detail.revision, comparison]);
  const [, render] = useState(0);
  const current = useRef<Inspection | null>(null);
  const onChangedRef = useRef(onChanged);
  const disabledRef = useRef(disabled);
  onChangedRef.current = onChanged;
  disabledRef.current = disabled;
  if (current.current?.scope !== scope) {
    if (current.current) current.current.active = false;
    current.current = { scope, active: true, checked: new Set(), photos: new Map(), generation: 0, pending: false, refreshPending: false, saved: false, stale: false, error: "", refreshError: "" };
  }
  const inspection = current.current;
  useEffect(() => {
    inspection.active = true;
    return () => { inspection.active = false; };
  }, [inspection]);
  const valid = () => current.current === inspection && inspection.active;
  const update = () => { if (valid()) render(value => value + 1); };
  if (!comparison) return null;

  const photoReady = (index: number, side: string) => inspection.photos.get(`${index}:${side}`)?.ready === true;
  const pairReady = (index: number) => photoReady(index, "submitted") && photoReady(index, "amazon");
  const allApproved = () => comparison.pairs.every((_, index) => pairReady(index) && inspection.checked.has(index));
  const locked = disabled || inspection.pending || inspection.refreshPending || inspection.saved || inspection.stale;
  const failedPhoto = [...inspection.photos.values()].some(photo => photo.failed);

  async function refresh(afterSave = false) {
    if (!valid() || disabledRef.current && !afterSave || inspection.refreshPending || inspection.pending && !inspection.saved) return;
    inspection.refreshPending = true;
    inspection.refreshError = "";
    update();
    try {
      await onChangedRef.current();
      if (valid() && !inspection.saved && !inspection.stale) {
        inspection.photos.clear();
        inspection.checked.clear();
        inspection.generation++;
      }
    } catch (error) {
      if (valid()) inspection.refreshError = `Status konnte nicht aktualisiert werden: ${getApiError(error)}`;
    } finally {
      if (valid()) { inspection.refreshPending = false; update(); }
    }
  }

  async function confirm() {
    if (!valid() || disabledRef.current || inspection.pending || inspection.refreshPending || inspection.saved || inspection.stale || !allApproved()) return;
    inspection.pending = true;
    inspection.error = "";
    update();
    try {
      await api.confirmImageReadback({ sellerSku: detail.sellerSku, marketplace: detail.marketplaceCode, expectedRevision: detail.revision, reviewToken: comparison!.reviewToken, jobId: comparison!.jobId, confirmed: true });
      if (!valid()) return;
      // A refresh failure must never turn a successful confirmation into another submission.
      inspection.saved = true;
      update();
      await refresh(true);
    } catch (error) {
      if (!valid()) return;
      inspection.error = getApiError(error);
      if (error && typeof error === "object" && "status" in error && error.status === 409) {
        inspection.stale = true;
        inspection.checked.clear();
      }
    } finally {
      if (valid()) { inspection.pending = false; update(); }
    }
  }

  function photo(index: number, side: "submitted" | "amazon", url: string, label: string) {
    const id = `${index}:${side}`;
    let entry = inspection.photos.get(id);
    if (!entry) { entry = { ready: false, failed: false }; inspection.photos.set(id, entry); }
    const state = entry;
    const bound = () => valid() && inspection.photos.get(id) === state;
    const fail = () => {
      if (!bound() || state.failed) return;
      state.ready = false;
      state.failed = true;
      inspection.checked.delete(index);
      update();
    };
    const loaded = (node: HTMLImageElement) => {
      if (!bound() || state.failed) return;
      if (!(node.naturalWidth > 0 && node.naturalHeight > 0)) { fail(); return; }
      if (!state.ready) { state.ready = true; update(); }
    };
    // Stable callbacks also handle cached images without accepting events from an old inspection.
    state.ref ??= node => { if (node?.complete) loaded(node); };
    state.onLoad ??= event => loaded(event.currentTarget);
    state.onError ??= fail;
    return <figure className="image-readback__photo">
      <figcaption>{label}</figcaption>
      <a href={url} target="_blank" rel="noreferrer" aria-label={`${label}: ${comparison!.pairs[index].role} vergrößern`}>
        <img key={`${scope}:${inspection.generation}:${id}`} src={url} alt={`${label}: ${comparison!.pairs[index].role}`} ref={state.ref} onLoad={state.onLoad} onError={state.onError}/>
      </a>
      {!state.ready && <span role={state.failed ? "alert" : "status"}>{state.failed ? "Bild konnte nicht geladen werden." : "Bild wird geladen …"}</span>}
    </figure>;
  }

  return <section className="image-readback" aria-label="Amazon-Bilder abgleichen">
    <h3>Amazon-Bilder abgleichen</h3>
    <p>Amazon hat die Übermittlung angenommen und andere Bildadressen zurückgegeben. Vergleiche die tatsächlichen Bilder. Deine Bestätigung gilt für deine Bildbeiträge bei Amazon. Sie bestätigt nicht die Anzeige auf der öffentlichen Produktseite.</p>
    <div className="image-readback__pairs">{comparison.pairs.map((pair, index) => <article className="image-readback__pair" key={`${pair.attribute}:${index}`}>
      <h4>{pair.role === "MAIN" ? "Hauptbild" : `Weiteres Bild (${pair.role})`}</h4>
      <div className="image-readback__photos">
        {photo(index, "submitted", pair.submittedUrl, "Übermittelt")}
        {photo(index, "amazon", pair.amazonUrl, "Von Amazon zurückgelesen")}
      </div>
      <label className="image-readback__check"><input type="checkbox" checked={inspection.checked.has(index)} disabled={locked || !pairReady(index)} onChange={event => {
        if (!valid() || disabledRef.current || inspection.pending || inspection.refreshPending || inspection.saved || inspection.stale || !pairReady(index)) return;
        if (event.target.checked) inspection.checked.add(index); else inspection.checked.delete(index);
        update();
      }}/>Beide Bilder zeigen dasselbe Motiv</label>
    </article>)}</div>
    {inspection.saved && <p role="status">Bestätigung gespeichert. Die Bildbeiträge wurden bestätigt.</p>}
    {inspection.stale && <p role="alert">Die Bilddaten haben sich geändert. Aktualisiere den Status und prüfe die aktuellen Bildpaare erneut.</p>}
    {inspection.error && <p role="alert">{inspection.error}</p>}
    {inspection.refreshError && <p role="alert">{inspection.refreshError}</p>}
    {failedPhoto && !inspection.saved && <p>Bitte lade die Bilder erneut, bevor du die Bildbeiträge bestätigst.</p>}
    <div className="image-readback__actions">
      {!inspection.saved && <button type="button" className="button button--primary" disabled={locked || !allApproved()} onClick={() => void confirm()}>{inspection.pending ? "Wird gespeichert …" : "Bildbeiträge bestätigen"}</button>}
      {(inspection.saved || inspection.stale || failedPhoto || inspection.error) && <button type="button" className="button button--secondary" disabled={disabled || inspection.pending || inspection.refreshPending} onClick={() => void refresh()}>{inspection.refreshPending ? "Wird aktualisiert …" : "Status aktualisieren"}</button>}
    </div>
  </section>;
}
